import { yupResolver } from "@hookform/resolvers/yup"
import { useEffect } from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"
import * as yup from "yup"

import { GoBack } from "@/components/shared/layout"
import { Button, DatePicker, Input, Label } from "@/components/ui"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import { sendHaptic } from "@/modules/helpers/utils"

// Validation schema
const validationSchema = yup.object({
  fullName: yup
    .string()
    .required("Họ và tên là bắt buộc")
    .min(2, "Họ và tên phải có ít nhất 2 ký tự"),
  email: yup.string().required("Email là bắt buộc").email("Email không hợp lệ"),
  phoneNumber: yup
    .string()
    .required("Số điện thoại là bắt buộc")
    .matches(/^[0-9]{10}$/, "Số điện thoại phải có 10 chữ số"),
  idCardNumber: yup
    .string()
    .required("Số căn cước công dân là bắt buộc")
    .matches(/^[0-9]{9,12}$/, "Số căn cước công dân phải có 9-12 chữ số"),
  issueDate: yup.string().required("Ngày cấp là bắt buộc"),
  issuePlace: yup.string().required("Nơi cấp là bắt buộc"),
  address: yup.string().required("Địa chỉ là bắt buộc")
})

type ContractFormData = {
  fullName: string
  email: string
  phoneNumber: string
  idCardNumber: string
  issueDate: string
  issuePlace: string
  address: string
}

const ContractPage = () => {
  const { user } = useCurrentUser()

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<ContractFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phoneNumber: "",
      idCardNumber: "",
      issueDate: "",
      issuePlace: "",
      address: ""
    }
  })

  // Pre-fill form with user data
  useEffect(() => {
    if (user) {
      reset({
        fullName: user?.profiles?.fullName || "",
        email: user?.email || "",
        phoneNumber: user?.profiles?.phoneNumber || "",
        idCardNumber: user?.profiles?.identifyNumber || "",
        issueDate: user?.profiles?.dateGrantIdentify || "",
        issuePlace: user?.profiles?.placeGrantIdentify || "",
        address: user?.profiles?.address || ""
      })
    }
  }, [user, reset])

  const onSubmit = async (data: ContractFormData) => {
    try {
      sendHaptic()
      // TODO: API call to update contract information
      console.log("Contract data:", data)
      toast.success("Cập nhật thông tin hợp đồng thành công!")
    } catch {
      toast.error("Có lỗi xảy ra khi cập nhật thông tin")
    }
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="sticky top-0 z-50">
        <GoBack title="Hợp đồng" className="bg-white" />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pb-6">
          {/* Full Name */}
          <div className="space-y-2">
            <Label required>Họ và tên</Label>
            <Controller
              name="fullName"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Nhập họ và tên"
                  className={errors.fullName ? "border-red-500" : ""}
                />
              )}
            />
            {errors.fullName && (
              <p className="text-xs text-red-500 px-1">{errors.fullName.message}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label required>Email</Label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="email"
                  placeholder="Nhập email"
                  className={errors.email ? "border-red-500" : ""}
                />
              )}
            />
            {errors.email && <p className="text-xs text-red-500 px-1">{errors.email.message}</p>}
          </div>

          {/* Phone Number */}
          <div className="space-y-2">
            <Label required>Số điện thoại</Label>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  placeholder="Nhập số điện thoại"
                  className={errors.phoneNumber ? "border-red-500" : ""}
                />
              )}
            />
            {errors.phoneNumber && (
              <p className="text-xs text-red-500 px-1">{errors.phoneNumber.message}</p>
            )}
          </div>

          {/* ID Card Number */}
          <div className="space-y-2">
            <Label required>Số căn cước công dân</Label>
            <Controller
              name="idCardNumber"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="tel"
                  placeholder="Nhập số căn cước công dân"
                  className={errors.idCardNumber ? "border-red-500" : ""}
                />
              )}
            />
            {errors.idCardNumber && (
              <p className="text-xs text-red-500 px-1">{errors.idCardNumber.message}</p>
            )}
          </div>

          {/* Issue Date */}
          <div className="space-y-2">
            <Label required>Ngày cấp</Label>
            <Controller
              name="issueDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value}
                  onChange={(date) => field.onChange(date)}
                  className={errors.issueDate ? "border-red-500" : ""}
                />
              )}
            />
            {errors.issueDate && (
              <p className="text-xs text-red-500 px-1">{errors.issueDate.message}</p>
            )}
          </div>

          {/* Issue Place */}
          <div className="space-y-2">
            <Label required>Nơi cấp</Label>
            <Controller
              name="issuePlace"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Nhập nơi cấp"
                  className={errors.issuePlace ? "border-red-500" : ""}
                />
              )}
            />
            {errors.issuePlace && (
              <p className="text-xs text-red-500 px-1">{errors.issuePlace.message}</p>
            )}
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label required>Địa chỉ</Label>
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Nhập địa chỉ"
                  className={errors.address ? "border-red-500" : ""}
                />
              )}
            />
            {errors.address && (
              <p className="text-xs text-red-500 px-1">{errors.address.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 text-white bg-gradient-primary font-medium rounded-2xl"
            >
              {isSubmitting ? "Đang xử lý..." : "Cập nhật thông tin"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ContractPage
